import { useEffect } from 'react'

import { DEFAULT_SOCIAL_IMAGE as DEFAULT_IMAGE, SITE_NAME, SITE_URL, SOCIAL_URLS } from '../lib/site'

type Props={
  title?:string
  description?:string
  image?:string|null
  canonicalPath?:string
  type?:'website'|'article'
  keywords?:string|null
  noindex?:boolean
  schemaType?:'WebSite'|'WebPage'|'Article'|'LearningResource'|'JobPosting'|'Course'
  datePublished?:string
}

function absoluteUrl(value?:string|null){
  if(!value)return DEFAULT_IMAGE
  if(/^https?:\/\//i.test(value))return value
  return `${SITE_URL}${value.startsWith('/')?'':'/'}${value}`
}

function upsertMeta(selector:string,attributes:Record<string,string>){
  let el=document.head.querySelector<HTMLMetaElement>(selector)
  if(!el){el=document.createElement('meta');document.head.appendChild(el)}
  Object.entries(attributes).forEach(([key,value])=>el!.setAttribute(key,value))
}

export default function Seo({title,description='Free HND notes, past papers, study resources, student notices, jobs, intakes and classes for Sri Lankan learners.',image,canonicalPath='/',type='website',keywords,noindex=false,schemaType='WebPage',datePublished}:Props){
  useEffect(()=>{
    const finalTitle=title?`${title} | ${SITE_NAME}`:SITE_NAME
    const canonical=absoluteUrl(canonicalPath)
    const finalImage=absoluteUrl(image)
    document.title=finalTitle
    upsertMeta('meta[name="description"]',{name:'description',content:description})
    upsertMeta('meta[name="robots"]',{name:'robots',content:noindex?'noindex,follow':'index,follow,max-image-preview:large,max-snippet:-1,max-video-preview:-1'})
    if(keywords)upsertMeta('meta[name="keywords"]',{name:'keywords',content:keywords});else document.head.querySelector('meta[name="keywords"]')?.remove()
    upsertMeta('meta[property="og:title"]',{property:'og:title',content:finalTitle})
    upsertMeta('meta[property="og:description"]',{property:'og:description',content:description})
    upsertMeta('meta[property="og:type"]',{property:'og:type',content:type})
    upsertMeta('meta[property="og:url"]',{property:'og:url',content:canonical})
    upsertMeta('meta[property="og:image"]',{property:'og:image',content:finalImage})
    upsertMeta('meta[property="og:site_name"]',{property:'og:site_name',content:SITE_NAME})
    upsertMeta('meta[name="twitter:card"]',{name:'twitter:card',content:'summary_large_image'})
    upsertMeta('meta[name="twitter:title"]',{name:'twitter:title',content:finalTitle})
    upsertMeta('meta[name="twitter:description"]',{name:'twitter:description',content:description})
    upsertMeta('meta[name="twitter:image"]',{name:'twitter:image',content:finalImage})
    let canonicalEl=document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]')
    if(!canonicalEl){canonicalEl=document.createElement('link');canonicalEl.rel='canonical';document.head.appendChild(canonicalEl)}
    canonicalEl.href=canonical

    const schema={
      '@context':'https://schema.org',
      '@type':schemaType,
      name:title||SITE_NAME,
      headline:title||SITE_NAME,
      description,
      url:canonical,
      image:finalImage,
      publisher:{'@type':'Organization',name:SITE_NAME,url:SITE_URL,logo:{'@type':'ImageObject',url:DEFAULT_IMAGE},sameAs:SOCIAL_URLS},
      ...(datePublished?{datePublished}:{}),
    }
    let script=document.head.querySelector<HTMLScriptElement>('script#hnd-seo-schema')
    if(!script){script=document.createElement('script');script.id='hnd-seo-schema';script.type='application/ld+json';document.head.appendChild(script)}
    script.textContent=JSON.stringify(schema)
  },[title,description,image,canonicalPath,type,keywords,noindex,schemaType,datePublished])
  return null
}
